/**
 * RefundRequest — one buyer's self-service refund attempt: the ask and its
 * answer on the same document. The browser decodes each uploaded ticket QR
 * on the device and writes only the decoded token, so no image ever leaves
 * it, and possession of that token is the entire proof of ownership.
 *
 * Path: `workspaces/{orgId}/refundRequests/{id}` — a client-minted random
 * id, so ownership is the `buyerUid` field rather than the path, and the
 * buyer's own-doc listen is this document's entire outcome mechanism (the
 * shape `Checkout` runs).
 *
 * The first decoded file creates the document carrying `buyerUid`,
 * `createdAt`, and `qrTokens`; every later pick or removal rewrites
 * `qrTokens` whole. `resolveRefundRequest` re-resolves the current token set
 * on each change and writes `resolvedAt`, `tickets`, and `refundableTotal`
 * back onto that same document, where the buyer is already listening. The
 * confirm is a compare-and-set on `confirmedAt` (null → request.time),
 * admitted only while `refundableTotal` is priced; `confirmRefundRequest`
 * re-resolves the tokens at spend time, creates `refundBatches/refund-{id}`,
 * and stamps `batchId` and `outcome` here. `refundRequestSweep` re-drives a
 * resolve or a confirm whose trigger died and mirrors the batch's progress
 * onto `outcome`. A TTL on `createdAt` retires the document.
 *
 * `read` is the only declared action. The create and both updates live in a
 * hand fragment (`ican.extraMatches`): the kernel's `set` write pattern
 * emits value equalities and no key mask at all, so a generated create
 * leaves the key set open and `MAX_REFUND_TOKENS` unenforced at the exact
 * boundary an anonymous session writes across; and a rule takes its action
 * from the verb it is declared under, so the confirm's CAS cannot be one.
 * Declaring only `read` keeps anything generated from competing with the
 * fragment — the same reason `Checkout`'s own header gives.
 */
import { entity } from "@mhmo91/ican";
import { firestore } from "@mhmo91/ican/firestore/binding";
import type { Timestamp } from "firebase/firestore";
import { z } from "zod";
import { SecretToken } from "./ticket.entity.ts";
import { Cents } from "./ticket-type.entity.ts";
import type { Actor } from "./workspace.entity.ts";

// Firestore caps an `in` disjunction at 30 values, and the resolver's one
// query is `collectionGroup('tickets').where('qrToken', 'in', qrTokens)` —
// the cap is that boundary, not a chosen number. The create fragment asserts
// it again as `qrTokens.size()`, because a zod `.max()` is not a trust
// boundary and an uncapped array kills the resolver on a crafted client.
export const MAX_REFUND_TOKENS = 30;

// How long a request may go unanswered before the work counts as stranded.
// Each trigger is one query and one transaction against warm documents, so a
// request still unanswered after two minutes is not slow — it is dead.
//
// One bound, three readers that have to agree: the sweep re-drives an
// unresolved request older than this measured from `createdAt` and an unbuilt
// confirm older than this measured from `confirmedAt`, and the buyer's page
// offers a resubmit at the same age. A page reading shorter tells the buyer it
// stalled while the sweep has not yet acted; a page reading longer spins past
// the moment the system already recovered.
export const ANSWER_STALL_MS = 2 * 60 * 1000;

// The eligibility vocabulary the buyer's page renders one row per. Only
// `valid` carries money; the other four each name a different reason the
// answer is no, and the buyer's next move differs by which one they read.
export const RefundVerdict = z
	.enum([
		"valid",
		"not-eligible",
		"already-refunded",
		"already-scanned",
		"not-found",
	])
	.describe(
		"the resolver's answer for one submitted token — not-found when nothing in this workspace carries it, already-scanned when the door already admitted the ticket, already-refunded when it is cancelled, not-eligible when the order is unpaid or the event's refund config does not cover it, valid when its share is refundable",
	);
export type RefundVerdict = z.infer<typeof RefundVerdict>;

// One row per token the requester submitted — the whole of what the buyer
// reads back. It names the ticket and its worth and nothing that identifies
// whose it is: whoever holds a forwarded ticket image already holds the
// token, and this row must not turn that into a name, an address, or an
// order id.
export const RefundRequestTicketSchema = z.object({
	qrToken: SecretToken.brand<"QrToken">().describe(
		"the submitted token this row answers — the page joins its rows to its own files on this value",
	),
	typeName: z
		.string()
		.nullish()
		.describe("the ticket type's own name; absent when nothing resolved"),
	amount: Cents.nullish().describe(
		"what this one ticket gives back, minor units — derived by Order.perTicketShares from the order's immutable lines, the same derivation the refund consumer spends, so the figure the buyer is shown and the figure that moves are one; absent unless the verdict is valid",
	),
	verdict: RefundVerdict,
});
export type RefundRequestTicket = z.infer<typeof RefundRequestTicketSchema>;

// refundRequests/{id} — one attempt, one document.
//
// Fields justified:
//   buyerUid        — the own-doc read key, rules-pinned to auth.uid at create
//   qrTokens        — the whole ask; possession of the QR is the entire proof
//   createdAt       — the retention TTL key, and the age the sweep's stall
//                     window is measured against
//   resolvedAt      — the resolver's liveness signal: the page offers a
//                     resubmit once it has not moved within the stall window
//   tickets         — what the buyer reads, one row per submitted token
//   refundableTotal — the figure on the button, and the confirm CAS's gate
//   confirmedAt     — the buyer's commitment, and the confirm trigger's edge
//   batchId         — the sweep's join to the batch the confirm built
//   outcome         — what the page says after the click
// Excluded:
//   buyerEmail — possession of the ticket QR is the whole ownership factor,
//     and an address on a document a forwarded image opens is a disclosure
//   selection (eventId/orderId/ticketIds) — server scratch state on a
//     buyer-readable document hands whoever holds one forwarded ticket image
//     the join key into an order; the confirm re-resolves the tokens itself,
//     which also re-checks eligibility at spend time
//   a status enum — every state this document has derives from a field
//     already here: unresolved ⟺ resolvedAt == null, a row still resolving ⟺
//     a token with no tickets[] entry, committed ⟺ confirmedAt != null,
//     settled ⟺ outcome. Nothing queries this collection, so no state is ever
//     a query predicate
export const RefundRequestSchema = z.object({
	buyerUid: z
		.string()
		.describe(
			"the session that created the request, rules-pinned to auth.uid — the own-doc read key, since the doc id is a client-minted random id rather than the uid",
		),
	qrTokens: z
		.array(SecretToken.brand<"QrToken">())
		.min(1)
		.max(MAX_REFUND_TOKENS)
		.describe(
			"the tokens decoded from the uploaded tickets — rewritten whole as files are picked and removed, never appended to, so a mis-picked file can be taken back out; the resolver dedupes before pricing",
		),
	createdAt: z
		.custom<Timestamp>()
		.describe(
			"rules-pinned == request.time at create — the retention TTL key, and what the sweep measures a stalled resolve against",
		),
	resolvedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe("when the resolver last priced the current token set"),
	tickets: z
		.array(RefundRequestTicketSchema)
		.nullish()
		.describe(
			"one row per token, written by the resolver — a token carrying no row yet is still being priced, which is the page's own per-row spinner",
		),
	refundableTotal: Cents.nullish().describe(
		"the sum of the valid rows, minor units — absent when nothing on this request is refundable, which is the predicate the confirm is admitted on: a request whose every token resolved to nothing must not be confirmable",
	),
	confirmedAt: z
		.custom<Timestamp>()
		.nullish()
		.describe(
			"the buyer's commitment, set once by a compare-and-set from null to request.time — the edge the confirm trigger fires on, and the point after which the token set is frozen",
		),
	batchId: z
		.string()
		.nullish()
		.describe(
			"the refund batch the confirm created — the join the sweep reads that batch's progress through",
		),
	outcome: z
		.enum(["running", "done", "partial", "failed"])
		.nullish()
		.describe(
			"how far the money has got, mirrored from the batch's own progress counters — running from the moment the batch exists, done when every order in it gave back, partial when some did and some failed, failed when none did; the page names a figure only once this lands",
		),
});
export type RefundRequest = z.infer<typeof RefundRequestSchema>;

// Own-doc read/listen — the buyer's entire outcome mechanism. The doc id is
// client-minted, so ownership is the buyerUid field, not the path. `list` is
// not declared and stays denied: the only key that yields anything is a
// token the asker already holds, and a queryable collection would make this
// document an oracle over every request in the workspace.
export const RefundRequest = entity<Actor>()({
	schema: RefundRequestSchema,
	actions: ["read"] as const,
})
	.pipe(({ can }) => [can("read").when((data, me) => data.buyerUid.eq(me.uid))])
	.adaptor(
		firestore({
			hierarchicalGrants: true,
			path: "workspaces/{orgId}/refundRequests/{id}",
		}),
	);
